import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { getHelpArticles } from '../services/api';

const HelpContext = createContext(null);

export function HelpProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('shortcuts'); // 'shortcuts' | 'help'
  const [filter, setFilter] = useState(null);              // { page, panel, slug } | null
  const [articles, setArticles] = useState([]);
  const loadedRef = useRef(false);
  const loadingRef = useRef(false);

  const ensureArticlesLoaded = useCallback(() => {
    if (loadedRef.current || loadingRef.current) return;
    loadingRef.current = true;
    getHelpArticles()
      .then(list => { setArticles(list); loadedRef.current = true; })
      .catch(() => {})
      .finally(() => { loadingRef.current = false; });
  }, []);

  const refreshArticles = useCallback(() => {
    loadedRef.current = false;
    ensureArticlesLoaded();
  }, [ensureArticlesLoaded]);

  const openHelp = useCallback(({ page, panel, slug } = {}) => {
    setFilter({ page, panel, slug });
    setActiveTab('help');
    setIsOpen(true);
    ensureArticlesLoaded();
  }, [ensureArticlesLoaded]);

  const toggleShortcuts = useCallback(() => {
    setIsOpen(v => {
      if (!v) setActiveTab('shortcuts');
      return !v;
    });
  }, []);

  const closeHelp = useCallback(() => setIsOpen(false), []);

  return (
    <HelpContext.Provider value={{
      available: true,
      isOpen, activeTab, setActiveTab, filter, articles,
      openHelp, toggleShortcuts, closeHelp,
      ensureArticlesLoaded, refreshArticles,
    }}>
      {children}
    </HelpContext.Provider>
  );
}

// What a component gets when it is mounted outside a HelpProvider: an inert
// context flagged as unavailable. Throwing instead — which this used to do —
// means any panel that renders a HelpButton can only ever be mounted inside the
// full app shell, and the failure is a whole-subtree ErrorBoundary rather than a
// missing button. The public track pages mount SpeedTrace and TrackMap on their
// own, and a help system that has no articles to show and no dialog to open is
// exactly the thing that should quietly not be there. Mirrors useTheme(), which
// has had a context-free fallback all along.
const HELP_UNAVAILABLE = {
  available: false,
  isOpen: false,
  activeTab: 'shortcuts',
  filter: null,
  articles: [],
  setActiveTab: () => {},
  openHelp: () => {},
  toggleShortcuts: () => {},
  closeHelp: () => {},
  ensureArticlesLoaded: () => {},
  refreshArticles: () => {},
};

export function useHelp() {
  return useContext(HelpContext) || HELP_UNAVAILABLE;
}
